import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { ChevronDown } from "lucide-react";
import { CategoryItemType } from "../models/category";
import { CategoryIcon } from "../utils/CategoryIcons";

export interface CategorySelectOption {
  value: CategoryItemType;
  label: string;
}

interface CategorySelectProps {
  options: CategorySelectOption[];
  value: CategoryItemType;
  onChange: (value: CategoryItemType) => void;
  disabled?: boolean;
  className?: string;
}

export function CategorySelect({
  options,
  value,
  onChange,
  disabled,
  className,
}: CategorySelectProps) {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedOption = options.find((option) => option.value === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      const selectedIndex = options.findIndex(
        (option) => option.value === value,
      );
      setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [open, options, value]);

  useEffect(() => {
    if (open) {
      const item = listRef.current?.children[highlightedIndex] as
        HTMLElement | undefined;
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [open, highlightedIndex]);

  function handleTriggerKeyDown(event: KeyboardEvent) {
    if (disabled) return;

    if (
      !open &&
      (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ")
    ) {
      event.preventDefault();
      setOpen(true);
      return;
    }

    if (!open) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((previous) =>
        Math.min(previous + 1, options.length - 1),
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((previous) => Math.max(previous - 1, 0));
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const option = options[highlightedIndex];
      if (option) {
        onChange(option.value);
        setOpen(false);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div className="category-select" ref={containerRef}>
      <button
        type="button"
        className={`category-select-trigger ${className ?? ""}`}
        onClick={() => setOpen((previous) => !previous)}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="category-select-trigger-content">
          {selectedOption && (
            <CategoryIcon itemType={selectedOption.value} size={16} />
          )}
          <span>{selectedOption?.label ?? ""}</span>
        </span>

        <ChevronDown
          size={16}
          className={`category-select-chevron ${
            open ? "category-select-chevron--open" : ""
          }`}
        />
      </button>

      {open && (
        <ul
          className="category-select-list"
          role="listbox"
          ref={listRef}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.preventDefault()}
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              className={`category-select-option ${
                option.value === value ? "category-select-option--active" : ""
              } ${
                index === highlightedIndex
                  ? "category-select-option--highlighted"
                  : ""
              }`}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              <CategoryIcon itemType={option.value} size={16} />
              <span>{option.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
